import { type FC } from "react";
import { Eye, Landmark, Lock } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";

import plaidLogo from "@/shared/assets/logos/plaid-logo.svg";

interface PermissionItemProps {
  icon: React.ReactNode;
  label: string;
}

const PermissionItem: FC<PermissionItemProps> = ({ icon, label }) => (
  <div className="flex items-center gap-2">
    <div className="shrink-0">{icon}</div>
    <p className="text-sm">{label}</p>
  </div>
);

interface PlaidPermissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContinue: () => void;
  isLoading?: boolean;
}

export const PlaidPermissionsDialog: FC<PlaidPermissionsDialogProps> = ({
  open,
  onOpenChange,
  onContinue,
  isLoading = false,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent size="sm">
      <DialogHeader>
        <div className="flex flex-col items-start gap-2">
          <img src={plaidLogo} alt="Plaid" className="h-12 w-12" />
          <DialogTitle>Connect the estate bank account</DialogTitle>
          <DialogDescription>
            Payments in and out show up here, so you can track the estate&apos;s bills and what it owes you back. We connect through Plaid, a bank-linking service, and the connection is encrypted.
          </DialogDescription>
        </div>
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-2 rounded-lg bg-subtle p-4">
          <PermissionItem icon={<Eye className="h-4 w-4 text-brand" />} label="We see balances and transactions" />
          <PermissionItem icon={<Landmark className="h-4 w-4 text-tertiary" />} label="We can't move money" />
          <PermissionItem icon={<Lock className="h-4 w-4 text-tertiary" />} label="We never see your bank password" />
        </div>
      </DialogBody>
      <div className="flex w-full justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="brand"
          onClick={onContinue}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Continue"}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);
